import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Printer, Loader2, Wallet, FileDown, Plus, RefreshCw } from "lucide-react";
import { api, fmtDate } from "@/lib/apiClient";
import { FakturSheet } from "@/components/FakturSheet";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/components/InvoiceDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MAX_LINES = 0; // tanpa batas — item berlebih otomatis lanjut lembar berikutnya
const INVOICE_ROWS = 30;

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out.length ? out : [[]];
};

const printInvoice = () => {
  const html = document.getElementById("invoice-a4-print")?.innerHTML;
  if (!html) return;
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return toast.error("Popup diblokir browser");
  const doc = w.document;
  const style = doc.createElement("style");
  style.textContent = `@page { size: A4; margin: 8mm; }
    body { font-family: Arial, sans-serif; color:#000; margin:0; font-size:12px; }
    table { width:100%; border-collapse:collapse; }
    .sheet { page-break-after: always; }
    .sheet:last-child { page-break-after: auto; }`;
  doc.head.appendChild(style);
  doc.body.innerHTML = html;
  w.setTimeout(() => w.print(), 400);
};

const Field = ({ label, children }) => (
  <div>
    <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </div>
);

const badge = (s) =>
  s === "LUNAS"
    ? "bg-emerald-500/15 text-emerald-300"
    : s === "SEBAGIAN"
    ? "bg-amber-500/15 text-amber-300"
    : "bg-rose-500/15 text-rose-300";

export const InvoiceA4Board = () => {
  const { user } = useAuth();
  const [cands, setCands] = useState([]);
  const [list, setList] = useState([]);
  const [sel, setSel] = useState([]);
  const [cari, setCari] = useState("");
  const [cariList, setCariList] = useState("");
  const [mode, setMode] = useState("detail");
  const [active, setActive] = useState(null);
  const [jumlah, setJumlah] = useState("");
  const [akun, setAkun] = useState("KAS");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([
        api.get("/invoices-a4/candidates"),
        api.get("/invoices-a4"),
      ]);
      setCands(c.data);
      setList(l.data);
    } catch {
      toast.error("Gagal memuat data invoice");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const q = cari.trim().toLowerCase();
  const candsView = useMemo(
    () => (q ? cands.filter((c) => (c.client_name || "").toLowerCase().includes(q)) : cands),
    [cands, q]
  );
  const listView = useMemo(() => {
    const ql = cariList.trim().toLowerCase();
    return ql ? list.filter((i) => (i.client_name || "").toLowerCase().includes(ql)) : list;
  }, [list, cariList]);

  const invPages = useMemo(
    () => (active ? chunk(active.lines || [], INVOICE_ROWS) : []),
    [active]
  );

  const estLines = useMemo(() => {
    if (mode === "ringkas") return sel.length;
    return cands.filter((c) => sel.includes(c.jo_number)).reduce((a, c) => a + (c.baris || 1), 0);
  }, [sel, mode, cands]);

  const toggle = (no) =>
    setSel((p) => (p.includes(no) ? p.filter((x) => x !== no) : [...p, no]));

  const buat = async () => {
    const clients = new Set(
      cands.filter((c) => sel.includes(c.jo_number)).map((c) => (c.client_name || "").trim().toLowerCase())
    );
    if (clients.size > 1) {
      toast.error("Silakan pilih JO dari client yang sama.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invoices-a4", { jo_numbers: sel, mode });
      toast.success(`Invoice ${data.invoice_no} dibuat · ${rupiah(data.total)}`);
      setActive(data);
      setSel([]);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat invoice");
    } finally {
      setBusy(false);
    }
  };

  const buka = async (no) => {
    try {
      const { data } = await api.get(`/invoices-a4/${no}`);
      setActive(data);
      setJumlah("");
    } catch {
      toast.error("Invoice tidak ditemukan");
    }
  };

  const bayar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices-a4/${active.invoice_no}/payment`, {
        jumlah: Number(jumlah) || 0,
        akun,
      });
      toast.success(`${data.jenis} ${rupiah(Number(jumlah))} tercatat · struk ${data.struk_no} · sisa ${rupiah(data.sisa)}`);
      setActive(data);
      setJumlah("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mencatat pembayaran");
    } finally {
      setBusy(false);
    }
  };

  const unduhPdf = async () => {
    try {
      const res = await api.get(`/invoices-a4/${active.invoice_no}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.invoice_no}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Gagal mengunduh PDF");
    }
  };

  return (
    <div className="space-y-5" data-testid="invoice-a4-board">
      <div className="glass rounded-2xl p-5 md:p-6 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Filter Nama Client">
            <Input
              data-testid="invoice-client-filter"
              placeholder="cari nama client"
              value={cari}
              onChange={(e) => setCari(e.target.value)}
              className="h-11 w-56 bg-slate-900/70 border-white/10"
            />
          </Field>
          <Field label="Mode Item">
            <select
              data-testid="invoice-mode-select"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="h-11 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none"
            >
              <option value="detail">Detail order (P × L × Qty)</option>
              <option value="ringkas">Ringkas (1 baris per JO)</option>
            </select>
          </Field>
          <div className="text-xs text-slate-400">
            Dipilih <b className="text-slate-200">{sel.length}</b> JO ·{" "}
            <span className="text-slate-200">{estLines} baris</span>{" "}
            <span className="text-slate-500">
              ({Math.max(Math.ceil(estLines / INVOICE_ROWS), 1)} lembar A4)
            </span>
          </div>
          <Button
            data-testid="invoice-create-btn"
            onClick={buat}
            disabled={busy || !sel.length}
            className="h-11 rounded-xl bg-blue-600 hover:bg-blue-500 px-5"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Buat Invoice A4
          </Button>
          <Button
            data-testid="invoice-refresh-btn"
            onClick={load}
            variant="outline"
            className="h-11 rounded-xl border-white/10 bg-slate-800/60"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {["", "No JO", "Tanggal", "Client", "Total", "Sisa", "Status"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="invoice-candidates">
              {candsView.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                    Tidak ada JO bernilai yang belum ditagih. Entri faktur di Papan Admin, atau JO
                    sudah masuk invoice lain.
                  </td>
                </tr>
              )}
              {candsView.map((c) => (
                <tr key={c.jo_number} className="border-t border-white/5 hover:bg-slate-800/40">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      data-testid={`invoice-pick-${c.jo_number}`}
                      checked={sel.includes(c.jo_number)}
                      onChange={() => toggle(c.jo_number)}
                      className="w-4 h-4 accent-blue-500"
                    />
                  </td>
                  <td className="px-3 py-2 font-semibold text-slate-200">{c.jo_number}</td>
                  <td className="px-3 py-2 text-slate-400">{fmtDate(c.tanggal)}</td>
                  <td className="px-3 py-2">{c.client_name}</td>
                  <td className="px-3 py-2">{rupiah(c.total)}</td>
                  <td className="px-3 py-2">{rupiah(c.sisa)}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-lg px-2 py-1 text-[10px] font-semibold ${badge(c.status)}`}>
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="glass rounded-2xl p-5 md:p-6 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-300">
            Daftar Invoice A4
          </h4>
          <Input
            data-testid="invoice-list-client-filter"
            placeholder="Filter nama client"
            value={cariList}
            onChange={(e) => setCariList(e.target.value)}
            className="h-10 w-56 bg-slate-900/70 border-white/10"
          />
        </div>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
              <tr>
                {["No Invoice", "Tanggal", "Client", "JO", "Total", "Terbayar", "Sisa", "Status", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="invoice-a4-list">
              {listView.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                    Belum ada invoice A4 dibuat.
                  </td>
                </tr>
              )}
              {listView.map((inv) => (
                <tr key={inv.invoice_no} className="border-t border-white/5 hover:bg-slate-800/40">
                  <td className="px-3 py-2 font-semibold text-blue-300">{inv.invoice_no}</td>
                  <td className="px-3 py-2 text-slate-400">{fmtDate(inv.tanggal)}</td>
                  <td className="px-3 py-2">{inv.client_name}</td>
                  <td className="px-3 py-2 text-slate-400">{(inv.jo_numbers || []).join(", ")}</td>
                  <td className="px-3 py-2">{rupiah(inv.total)}</td>
                  <td className="px-3 py-2">{rupiah(inv.terbayar)}</td>
                  <td className="px-3 py-2">{rupiah(inv.sisa)}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-lg px-2 py-1 text-[10px] font-semibold ${badge(inv.status)}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      data-testid={`invoice-open-${inv.invoice_no}`}
                      onClick={() => buka(inv.invoice_no)}
                      className="rounded-lg border border-white/10 bg-slate-800/70 px-3 py-1 text-[10px] font-semibold uppercase hover:bg-slate-700"
                    >
                      Buka
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!active ? (
        <div className="glass rounded-2xl p-10 text-center text-slate-500" data-testid="invoice-a4-empty">
          Pilih JO lalu buat invoice, atau buka invoice yang sudah ada untuk pratinjau A4.
        </div>
      ) : (
        <>
          <div className="glass rounded-2xl p-5 md:p-6 flex flex-wrap items-end gap-4">
            <Field label="Nominal Pembayaran">
              <Input
                data-testid="invoice-amount-input"
                placeholder={`maks ${rupiah(active.sisa)}`}
                value={jumlah}
                onChange={(e) => setJumlah(e.target.value)}
                className="h-11 w-40 bg-slate-900/70 border-white/10"
              />
            </Field>
            <Field label="Via">
              <select
                data-testid="invoice-akun-select"
                value={akun}
                onChange={(e) => setAkun(e.target.value)}
                className="h-11 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none"
              >
                {["KAS", "BANK"].map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </Field>
            <Button
              data-testid="invoice-pay-btn"
              onClick={bayar}
              disabled={busy || active.sisa <= 0 || !jumlah}
              className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-5"
            >
              <Wallet className="w-4 h-4 mr-2" /> Payment
            </Button>
            <Button
              data-testid="invoice-print-btn"
              onClick={printInvoice}
              variant="outline"
              className="h-11 rounded-xl border-white/10 bg-slate-800/60 px-5"
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button
              data-testid="invoice-pdf-btn"
              onClick={unduhPdf}
              variant="outline"
              className="h-11 rounded-xl border-white/10 bg-slate-800/60 px-5"
            >
              <FileDown className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </div>

          <div className="glass rounded-2xl p-6 overflow-x-auto">
            <div
              id="invoice-a4-print"
              data-testid="invoice-a4-preview"
              className="mx-auto w-fit space-y-6"
            >
              {invPages.map((rows, idx) => (
                <div
                  key={`ip-${idx}`}
                  className="sheet bg-white"
                  data-testid={`invoice-sheet-${idx + 1}`}
                  style={{ width: 790, padding: 18 }}
                >
                  <FakturSheet
                    nomor={active.invoice_no}
                    label="INVOICE"
                    tanggal={fmtDate(active.tanggal)}
                    tanggalPrint={new Date().toLocaleDateString("id-ID")}
                    client={active.client_name}
                    lines={rows}
                    rowCount={INVOICE_ROWS}
                    total={active.total}
                    terbayar={active.terbayar}
                    sisa={active.sisa}
                    showTotals={idx === invPages.length - 1}
                    catatan={`No JO: ${(active.jo_numbers || []).join(", ")}${
                      active.client_phone ? ` · HP: ${active.client_phone}` : ""
                    }`}
                    printedBy={user?.username || ""}
                    pageInfo={
                      invPages.length > 1
                        ? `Lembar ${idx + 1} dari ${invPages.length} · Status: ${active.status}`
                        : `Status: ${active.status}`
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
